<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        style="max-width:36px !important"
        icon
        @click.stop=""
        v-on="on"
      >
        <v-icon color="hrissecondary">
          mdi-plus-circle
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Beneficiaries"
      text="Create beneficiaries"
      class="mb-0"
      :loading="loading"
    > 
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col
              v-if="filteredDependents.length > 0"
              cols="12"
              class="pb-0"
            >
              <v-switch
                v-model="sync"
                label="Sync from immediate families list"
                @change="resetValues"
              />
            </v-col>
            <v-col
              v-if="sync"
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                v-model="dependent"
                v-validate="'required|max:80'"
                label="Selected Dependent's Name"
                :error-messages="errors.collect('dependent')"
                data-vv-name="dependent"  
                outlined
                :items="filteredDependents"
                item-text="name"
                item-value="name"
                return-object
                :class="{
                  'required':!dependent
                }"
                @change="setValues(dependent)"
              />
            </v-col>
            <v-col
              v-else
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="name"
                v-validate="'required|max:80|nameAllowedFormat'"
                label="Name"
                :error-messages="errors.collect('name')"
                data-vv-name="name"  
                outlined
                :class="{
                  'required':!name
                }"
                @input="name = name.toUpperCase()"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <span> Citizenship? </span>
              <v-radio-group
                v-model="isCitizen"
                class="mt-0"
                row
                @change="ic=null,passportNo=null"
              >
                <v-radio
                  label="Non-citizen"
                  :value="0"
                />
                <v-radio
                  label="Citizen"
                  :value="1"
                /> 
              </v-radio-group>
            </v-col>
            
            <v-col
              cols="6"
              class="pb-0"
            >
              <v-text-field 
                v-show="isCitizen === 1"
                v-model="ic"
                v-validate="isCitizen === 1 ? 'required|icDate|icLength' : ''"
                v-mask="'######-##-####'"
                label="IC"
                :error-messages="errors.collect('ic')"
                data-vv-name="ic"
                data-vv-as="IC"
                outlined
                :class="{
                  'required':!ic && isCitizen === 1
                }"
              />
              <v-text-field
                v-show="isCitizen === 0"
                v-model="passportNo"
                v-validate="isCitizen === 0 ? 'required' : ''"
                label="Passport No."
                :error-messages="errors.collect('isCitizen')"
                data-vv-name="isCitizen"
                data-vv-as="passport number"
                outlined
                :class="{
                  'required':!passportNo && isCitizen === 0
                }"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-autocomplete
                v-model="relationship"
                v-validate="'required'"
                :items="relationships"
                label="Relationship"
                :error-messages="errors.collect('relationship')"
                data-vv-name="relationship"    
                data-vv-as="relationship"     
                item-text="name"
                item-value="id"
                outlined
                :class="{
                  'required':!relationship
                }"
              />
            </v-col>

            <v-col
              cols="6"
              class="pb-0"
            >
              <v-text-field
                v-model="percentageOfShare"
                v-validate="'required|min_value:1|max_value:100|totalShareNotExceed'"
                :error-messages="errors.collect('percentageOfShare')"
                data-vv-name="percentageOfShare"
                name="percentageOfShare"
                data-vv-as="percentage of share"
                label="% of Share"
                outlined
                type="number"
                min="1"
                max="100"
                suffix="%"
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"           
          @click="submitForm"
        >
          Save
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String,
        required: true,
      },
      beneficiaries: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        dialog: null,
        loading: false,
        name: null,
        isCitizen: 1,
        ic: null,
        passportNo: null,
        percentageOfShare: 0,
        dependents: [],
        relationships: [],
        relationship: null,
        dependent: null,
        sync: 0,
      }
    },
    computed: {
      filteredDependents () {
        return this.dependents.filter(dep => {
          return !this.beneficiaries.some(b => {
            return (
              (dep.ic && b.ic && dep.ic === b.ic) ||
              (dep.passport_no && b.passport_no && dep.passport_no === b.passport_no)
            )
          })
        })
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()
        
        this.$validator.extend('totalShareNotExceed', {
          getMessage () {
            return 'Total percentage of share cannot exceed 100%'
          },
          validate: (value) => {
            const num = Number(value || 0)
            const existing = this.beneficiaries.reduce(
              (sum, b) => sum + Number(b.percentage_of_share || 0),
              0
            )
            return existing + num <= 100
          },
        })
        
        this.$validator.extend('icDate', {
          getMessage (field, args) {
            return 'Invalid IC Date'        
          },
          validate (value, args) {
            var y = parseInt(value.substr(0, 2))
            var m = parseInt(value.substr(2, 2))
            var d = parseInt(value.substr(4, 2))
            if (m === 0 || m > 12) { return false }
            var monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ]
            // Adjust for leap years
            if (y % 400 === 0 || (y % 100 !== 0 && y % 4 === 0)) { monthLength[1] = 29 }
            return d > 0 && d <= monthLength[m - 1]
          },
        })

        this.$validator.extend('icLength', {
          getMessage (field, args) {
            return 'IC not in right length'        
          },
          validate (value, args) {
            return value.toString().length === 14
          },
        })

        this.$validator.extend('nameAllowedFormat', {
          getMessage (field, args) {
            return "Invalid name format. Only alphabet and special character of @ ' * - / . allowed for name"     
          },
          validate (value, args) {
            const regex = /^[ A-Za-z'@./*-]*$/
            return regex.test(value)
          },
        })

        this.loading = true
     
        Promise.all([
          this.$api.hrisRelationships().list(),
          this.$api.hrisDependents().list(this.employeeUid),
        ])
          .then(([relationships, dependents]) => {
            this.relationships = relationships.data
            this.dependents = dependents.data

            if (this.filteredDependents.length > 0) {
              this.sync = 1
            }

            this.loading = false
          })
      },
    },
    methods: {
      resetValues () {
        this.dependent = null
        this.name = null
        this.ic = null
        this.passportNo = null
        this.relationship = null
        this.percentageOfShare = 0
        this.isCitizen = 1
      },
      setValues (dependent) {
        this.name = dependent.name
        this.ic = dependent.ic
        this.passportNo = dependent.passport_no
        this.relationship = dependent.relationship_id
        this.percentageOfShare = 0
        this.isCitizen = dependent.ic ? 1 : 0
      },
      prepareFormData () {
        return {
          name: this.name.toUpperCase(),
          relationship_id: this.relationship,
          is_citizen: this.isCitizen,
          ic: this.ic ? this.ic.replace(/-/g, '') : null,
          passport_no: this.passportNo ? this.passportNo : null,
          percentage_of_share: this.percentageOfShare,
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.setProgressing(true)
    
            this.$api.beneficiaries().create(this.employeeUid, this.prepareFormData())
              .then((response) => {
                if (response.status === 200 || response.status === 201) {
                  this.$emit('created')                
                  this.$snack({ message: response.data.status })

                  this.setProgressing(false)
                  this.dialog = false       
                }
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },

    },
  }
</script>

<style scoped>
.required >>> fieldset {
  border: 2px solid red;
}
</style>
